import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  BookOpen, Package, Users, AlertTriangle, Clock, CheckCircle2,
  ArrowRight, Truck, Lock, Calendar, ChevronRight, Info, Sparkles,
  ArrowDown, ArrowUp, GitBranch, Link2, DollarSign
} from "lucide-react";

const typeConfig = {
  'builder-scope': { label: 'Builder scope', badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package, color: '#3b82f6' },
  'excluded': { label: 'Excluded', badge: 'bg-slate-100 text-slate-600 border-slate-200', icon: Info, color: '#94a3b8' },
  'owner-supplied': { label: 'Owner-supplied', badge: 'bg-purple-100 text-purple-700 border-purple-200', icon: Users, color: '#8b5cf6' },
  'owner-appointed': { label: 'Owner-appointed', badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: Truck, color: '#f59e0b' },
};

const statusConfig = {
  active: { label: 'Active', badge: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pending', badge: 'bg-amber-100 text-amber-700' },
  booked: { label: 'Booked', badge: 'bg-blue-100 text-blue-700' },
  'not-booked': { label: 'Not booked', badge: 'bg-red-100 text-red-700' },
};

function DependencyNode({ item, isSource, onClick }) {
  const tc = typeConfig[item.type] || typeConfig['builder-scope'];
  const Icon = tc.icon;
  return (
    <button onClick={() => onClick(item)} className={`flex items-center gap-2.5 p-3 rounded-xl border-2 ${isSource ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 bg-white hover:border-slate-300'} transition-all w-full text-left`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${tc.color}15` }}>
        <Icon className="w-4 h-4" style={{ color: tc.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-900 truncate">{item.item}</p>
        <p className="text-[10px] text-slate-400">{tc.label} &middot; {item.owner || item.type}</p>
      </div>
      <Badge className={`text-[8px] ${tc.badge} border shrink-0`}>{tc.label}</Badge>
    </button>
  );
}

function DependencyGraph({ items, selected, onSelect }) {
  const ownerSupplied = items.filter(i => i.type === 'owner-supplied');
  const ownerAppointed = items.filter(i => i.type === 'owner-appointed');
  const builderScope = items.filter(i => i.type === 'builder-scope');
  const excluded = items.filter(i => i.type === 'excluded');

  // Build dependency chains
  const chains = ownerSupplied.map(os => ({
    source: os,
    dependents: ownerAppointed.filter(oa => {
      if (os.item.toLowerCase().includes('tile') && oa.item.toLowerCase().includes('tiler')) return true;
      if (os.item.toLowerCase().includes('kitchen') && oa.item.toLowerCase().includes('cabinet')) return true;
      return false;
    }),
    builderImpact: builderScope.filter(bs => {
      if (os.item.toLowerCase().includes('tile') && bs.item.toLowerCase().includes('plumb')) return true;
      return false;
    }),
  })).filter(c => c.dependents.length > 0 || c.builderImpact.length > 0);

  return (
    <div className="space-y-4" data-testid="dependency-graph">
      <div className="flex items-center gap-2 mb-2">
        <GitBranch className="w-4 h-4 text-indigo-600" />
        <span className="text-xs font-bold text-indigo-800">Dependency chains</span>
        <span className="text-[10px] text-indigo-500">Owner items that affect readiness</span>
      </div>

      {chains.length > 0 ? chains.map((chain, ci) => (
        <div key={ci} className="bg-gradient-to-b from-indigo-50/30 to-white border border-indigo-100 rounded-xl p-4 space-y-3">
          <DependencyNode item={chain.source} isSource onClick={onSelect} />
          {chain.dependents.length > 0 && (
            <>
              <div className="flex items-center gap-2 pl-6">
                <ArrowDown className="w-4 h-4 text-indigo-400" />
                <span className="text-[10px] text-indigo-500 font-medium">depends on</span>
              </div>
              {chain.dependents.map((dep, di) => (
                <div key={di} className="ml-6"><DependencyNode item={dep} isSource={false} onClick={onSelect} /></div>
              ))}
            </>
          )}
          {chain.builderImpact.length > 0 && (
            <>
              <div className="flex items-center gap-2 pl-6">
                <ArrowDown className="w-4 h-4 text-amber-400" />
                <span className="text-[10px] text-amber-600 font-medium">impacts builder scope</span>
              </div>
              {chain.builderImpact.map((bi, bii) => (
                <div key={bii} className="ml-6"><DependencyNode item={bi} isSource={false} onClick={onSelect} /></div>
              ))}
            </>
          )}
          {chain.source.readinessImpact && (
            <div className="ml-6 p-2.5 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
              <span className="text-[10px] text-red-700">{chain.source.readinessImpact}</span>
            </div>
          )}
        </div>
      )) : (
        <Card className="border-slate-200"><CardContent className="py-6 text-center text-xs text-slate-400">No dependency chains detected for current scope items</CardContent></Card>
      )}
    </div>
  );
}

export default function ScopeBoundaryLedger() {
  const { scopeItems, workspace, analytics, activeLens, logAction } = useDemoState();
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('all');

  const counts = useMemo(() => ({
    builder: scopeItems.filter(i => i.type === 'builder-scope').length,
    excluded: scopeItems.filter(i => i.type === 'excluded').length,
    ownerSupplied: scopeItems.filter(i => i.type === 'owner-supplied').length,
    ownerAppointed: scopeItems.filter(i => i.type === 'owner-appointed').length,
    pendingItems: scopeItems.filter(i => i.status === 'pending' || i.status === 'not-booked').length,
  }), [scopeItems]);

  const filtered = tab === 'all' ? scopeItems : scopeItems.filter(i => i.type === tab);

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="scope-boundary-page">
        {/* Warning */}
        {counts.pendingItems > 0 && (
          <Card className="border-amber-200 bg-amber-50/50" data-testid="scope-warning">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">{counts.pendingItems} owner-side item(s) pending</p>
                <p className="text-[10px] text-amber-700">Late owner-supplied items and unbooked owner-appointed trades can delay readiness.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Builder scope", value: counts.builder, icon: Package, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
            { label: "Excluded", value: counts.excluded, icon: Info, color: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" },
            { label: "Owner-supplied", value: counts.ownerSupplied, icon: Users, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
            { label: "Owner-appointed", value: counts.ownerAppointed, icon: Truck, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
          ].map(s => (
            <Card key={s.label} className={`${s.bg} ${s.border} border`}>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div><p className={`text-2xl font-heading font-bold ${s.color}`}>{s.value}</p><p className="text-[10px] text-slate-500">{s.label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Owner-side risk analytics */}
        {analytics && analytics.ownerRisk.length > 0 && activeLens !== 'client' && activeLens !== 'buyer' && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="scope-analytics">
            <div className="flex items-center gap-1.5 bg-purple-50 border border-purple-100 rounded-lg px-3 py-1.5">
              <AlertTriangle className="w-3 h-3 text-purple-500" />
              <span className="text-[10px] font-semibold text-purple-700">{analytics.ownerRisk.length} owner-side item(s) at risk</span>
            </div>
            {analytics.ownerRisk.slice(0, 2).map((r, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                <Clock className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] font-semibold text-amber-700">{r.item}</span>
              </div>
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 h-8" data-testid="scope-tabs">
            <TabsTrigger value="all" className="text-xs">All ({scopeItems.length})</TabsTrigger>
            <TabsTrigger value="builder-scope" className="text-xs">Builder</TabsTrigger>
            <TabsTrigger value="owner-supplied" className="text-xs">Owner-supplied</TabsTrigger>
            <TabsTrigger value="owner-appointed" className="text-xs">Owner-appointed</TabsTrigger>
            <TabsTrigger value="graph" className="text-xs">Dependencies</TabsTrigger>
          </TabsList>

          <TabsContent value="graph" className="mt-4">
            <DependencyGraph items={scopeItems} selected={selected} onSelect={setSelected} />
          </TabsContent>

          {['all', 'builder-scope', 'owner-supplied', 'owner-appointed', 'excluded'].map(tabVal => (
            <TabsContent key={tabVal} value={tabVal} className="mt-4">
              <div className="space-y-2">
                {filtered.map(item => {
                  const tc = typeConfig[item.type] || typeConfig['builder-scope'];
                  const sc = statusConfig[item.status];
                  const Icon = tc.icon;
                  return (
                    <Card key={item.id} className="border-slate-200 hover:shadow-sm transition-all cursor-pointer" onClick={() => setSelected(item)} data-testid={`scope-item-${item.id}`}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${tc.color}15` }}>
                              <Icon className="w-4 h-4" style={{ color: tc.color }} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900">{item.item}</p>
                              <p className="text-[10px] text-slate-500">{item.category} &middot; {item.project}</p>
                              {item.dependency && <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1"><Link2 className="w-3 h-3" /> {item.dependency}</p>}
                              {item.readinessImpact && <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {item.readinessImpact}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={`text-[9px] border ${tc.badge}`}>{tc.label}</Badge>
                            {sc && <Badge className={`text-[9px] ${sc.badge}`}>{sc.label}</Badge>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {filtered.length === 0 && <Card className="border-slate-200"><CardContent className="py-6 text-center text-xs text-slate-400">No items in this category</CardContent></Card>}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="scope-detail-drawer">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle>{selected.item}</SheetTitle>
                  <SheetDescription>{typeConfig[selected.type]?.label} &middot; {selected.category}</SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  {[
                    { label: "Project", value: selected.project },
                    { label: "Owner", value: selected.owner },
                    { label: "Status", value: statusConfig[selected.status]?.label || selected.status },
                    selected.dueDate && { label: "Due date", value: selected.dueDate },
                    selected.noticeDate && { label: "Notice date", value: selected.noticeDate },
                  ].filter(Boolean).map((r, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-slate-100"><span className="text-xs text-slate-500">{r.label}</span><span className="text-xs font-medium text-slate-700">{r.value}</span></div>
                  ))}
                  {selected.dependency && (
                    <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Dependency</p>
                      <p className="text-xs text-amber-700">{selected.dependency}</p>
                    </div>
                  )}
                  {selected.readinessImpact && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-xs font-semibold text-red-800 mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Readiness impact</p>
                      <p className="text-xs text-red-700">{selected.readinessImpact}</p>
                    </div>
                  )}
                  {selected.notes && <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Notes</p><p className="text-xs text-slate-600">{selected.notes}</p></div>}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
